import ICustomerSalesProduct from "@/interfaces/customer-sales-product.interfaces";
import { CustomerSalesProductStatus } from "@/types/customer-sales-product.types";

/** Plano ativo para permissões, billing gate e bloqueio de nova contratação. */
export const isActivePlanStatus = (status: CustomerSalesProductStatus): boolean =>
  status === CustomerSalesProductStatus.Active;

/** Assinatura vigente na UI (ativa ou aguardando pagamento). */
export const isContractedPlanStatus = (status: CustomerSalesProductStatus): boolean =>
  status === CustomerSalesProductStatus.Active ||
  status === CustomerSalesProductStatus.HoldDuePaymentIssue;

export const findContractedPlan = (
  customerSalesProducts: ICustomerSalesProduct[]
): ICustomerSalesProduct | undefined =>
  customerSalesProducts.find((product) => isContractedPlanStatus(product.status));

/** Plano A ou H: bloqueia nova contratação (alinhado à API). */
export const hasContractedPlan = (customerSalesProducts: ICustomerSalesProduct[]): boolean => {
  if (!customerSalesProducts || customerSalesProducts.length === 0) {
    return false;
  }

  return customerSalesProducts.some((product) => isContractedPlanStatus(product.status));
};

export const hasActivePlan = (customerSalesProducts: ICustomerSalesProduct[]): boolean => {
  if (!customerSalesProducts || customerSalesProducts.length === 0) {
    return false;
  }

  return customerSalesProducts.some((product) => isActivePlanStatus(product.status));
};

/** Sem plano com Status A (não usa mais EndDate). */
export const allPlansExpired = (customerSalesProducts: ICustomerSalesProduct[]): boolean => {
  if (!customerSalesProducts || customerSalesProducts.length === 0) {
    return false;
  }

  return !hasActivePlan(customerSalesProducts);
};

export const hasOverduePayment = (
  customerSalesProducts: ICustomerSalesProduct[] | null
): ICustomerSalesProduct[] => {
  if (!customerSalesProducts || customerSalesProducts.length === 0) {
    return [];
  }

  return customerSalesProducts.filter((product) => {
    const status = String(product.status ?? "").toUpperCase();
    return status === CustomerSalesProductStatus.HoldDuePaymentIssue;
  });
};

/** Plano contratado aguardando pagamento (Status H). */
export const hasPendingPaymentPlan = (
  customerSalesProducts: ICustomerSalesProduct[] | null
): boolean => hasOverduePayment(customerSalesProducts).length > 0;

/** Sem plano ativo e sem pendência de pagamento (ex.: cancelado/expirado) → fluxo de renovação. */
export const needsPlanRenewal = (
  customerSalesProducts: ICustomerSalesProduct[] | null
): boolean => {
  if (!customerSalesProducts || customerSalesProducts.length === 0) {
    return false;
  }

  return allPlansExpired(customerSalesProducts) && !hasPendingPaymentPlan(customerSalesProducts);
};
